import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import aduBedroomsImage from '@/assets/adu-bedrooms.jpg';

const HowManyBedroomsCanADUHaveCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/how-many-bedrooms-adu-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={aduBedroomsImage} 
              alt="ADU floor plans with different bedroom layouts" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              How Many Bedrooms Can an ADU Have in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Discover bedroom limits and rules for ADUs in California, including maximum allowances, city-specific requirements, and space optimization tips.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Bedroom Limits in California ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                California ADUs typically accommodate 1-2 bedrooms depending on total square footage and local regulations. Most jurisdictions allow up to 2 bedrooms in larger ADUs (800+ square feet), while smaller units are often limited to 1 bedroom or studio configurations. Bedroom count affects parking requirements, occupancy limits, and utility demands.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Typical ADU Layouts and Bedroom Counts
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Studio ADUs (400-600 sq ft) maximize open space without separate bedrooms. One-bedroom ADUs (600-800 sq ft) offer privacy and rental appeal. Two-bedroom ADUs (900-1,200 sq ft) accommodate families or higher rental income but require careful space planning to maintain livability and code compliance within size restrictions.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                City-specific Regulations and Tips
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Cities like San Jose and Los Angeles may restrict bedroom counts based on lot size and parking availability. Some jurisdictions require additional parking spaces for multi-bedroom ADUs. Design strategies include convertible spaces, murphy beds, and flexible room layouts that maximize functionality while meeting bedroom count regulations and building code requirements.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: What's the maximum number of bedrooms allowed in an ADU?</p>
                  <p>A: Typically 2 bedrooms, though larger units may be possible in certain cities.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Do ADU bedroom limits vary by city?</p>
                  <p>A: Yes, local zoning rules often specify bedroom and occupancy limits.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HowManyBedroomsCanADUHaveCalifornia;